import React, { useMemo } from "react";
import {
  WithdrawalProcessSteps,
  useWithdrawalProcess,
} from "../../useWithdrawalProcess";
import { Button, Divider, Space, Typography } from "antd";
import Big from "big.js";
import {
  IBankAccountProperties,
  IPrivateWallets,
} from "@funds/components/WithdrawAmount/IWithdrawAmount";
import NavigationFooter from "../NavigationFooter";
import Checkbox from "@appComponents/Checkbox/Checkbox";
import { MAIN_CURRENCY, MAIN_CURRENCY_LABEL } from "@bundles/consts";
import { IWithdrawFees } from "@funds/components/WithdrawForm/IWithdrawForm";
import Tooltip from "@funds/pages/MyFunds/parts/Tooltip";
import { CRYPTO_PLACES } from "@appComponents/CurrencyElement/CurrencyElement";

import * as style from "./SummaryStyles.module.scss";

type Props = {
  fees: IWithdrawFees[];
};

const Summary = ({ fees }: Props) => {
  const { state, setState } = useWithdrawalProcess();
  const isMainCurrency = state.currency === MAIN_CURRENCY;

  const isCryptoWithdrawal = state.selectedMethod !== "bankTransfer";

  const currencyLabel = () => {
    if (isCryptoWithdrawal) {
      const label =
        state.currency === MAIN_CURRENCY ? MAIN_CURRENCY_LABEL : state.currency;
      return label?.toLocaleUpperCase();
    }

    return "USD";
  };

  const getCurrency = () => {
    if (isCryptoWithdrawal) {
      const currency = state.currency === "bfusd" ? "usdt" : state.currency;

      return fees.find((el) => el.currency === currency)?.currency;
    }

    return state.currency;
  };

  const currency = getCurrency();

  const flatFee =
    fees.find((el) => el.currency === currency)?.fees.flat_fee ?? 0;

  const transactionFee = isCryptoWithdrawal
    ? flatFee
    : Number(state.amount) <= 10_000
    ? 100
    : Number(state.amount) * 0.01;

  const mainData = useMemo(() => {
    return [
      {
        label: "Withdrawal Amount",
        value: `${state.amount} ${currencyLabel()}`,
      },

      {
        label: isCryptoWithdrawal ? (
          <Tooltip
            tooltipMessage="This is an estimation using current blockchain situation. The actual Fee will be charged at the time the withdrawal is processed and can vary based on blockchain congestion."
            title="Estimated Transaction fee"
          />
        ) : (
          "Withdrawal Fee"
        ),
        value:
          Big(flatFee).eq(0) && isCryptoWithdrawal
            ? "Blockchain fees (Deducted in kind)"
            : `${Big(transactionFee).round(
                isMainCurrency ? 2 : CRYPTO_PLACES
              )} ${currencyLabel()}`,
      },
      {
        label: "Withdrawal method",
        value: `${isCryptoWithdrawal ? "Crypto Wallet" : "Bank Transfer"}`,
      },
    ];
  }, [state]);

  const destinationData = useMemo(() => {
    if (isCryptoWithdrawal) {
      const selectedCryptoWallet =
        state.selectedCryptoWallet as IPrivateWallets;

      return [
        {
          label: "Wallet address",
          value: selectedCryptoWallet?.address,
        },
        {
          label: "wallet name",
          value: selectedCryptoWallet?.name ?? "-",
        },
        {
          label: "Currency",
          value: currencyLabel(),
        },
      ];
    }

    const selectedCryptoWallet =
      state.selectedCryptoWallet as IBankAccountProperties;
    return [
      {
        label: "Bank BIC/SWIFT",
        value: selectedCryptoWallet?.bic_swift,
      },

      {
        label: "Bank Account Country",
        value: selectedCryptoWallet?.country,
      },

      {
        label: "Bank Account City",
        value: selectedCryptoWallet?.city,
      },
      {
        label: "Account Number",
        value: selectedCryptoWallet?.account_number,
      },
      {
        label: "Account Beneficiary Name",
        value: selectedCryptoWallet?.holder_name,
      },
      {
        label: "Account Beneficiary Address",
        value: selectedCryptoWallet?.holder_address.line_1,
      },
      {
        label: "Account Beneficiary Address(cont.)",
        value: selectedCryptoWallet?.holder_address.line_2,
      },
      {
        label: "Account Beneficiary Postcode",
        value: selectedCryptoWallet?.holder_address.postcode,
      },
      {
        label: "Account Beneficiary County",
        value: selectedCryptoWallet?.holder_address.county,
      },
    ];
  }, [state]);

  const onContinue = () =>
    setState({
      ...state,
      step: WithdrawalProcessSteps.MFA_CODE,
    });

  const [usdtCheckboxAgreements, setUsdtCheckboxAgreements] = React.useState({
    agreement1: false,
    agreement2: false,
  });

  const nextDisabled = useMemo(() => {
    if (isCryptoWithdrawal) return false;
    return (
      !usdtCheckboxAgreements.agreement1 || !usdtCheckboxAgreements.agreement2
    );
  }, [usdtCheckboxAgreements]);

  const onUsdtToFiatAgreement = (name: string) => {
    setUsdtCheckboxAgreements((prev: any) => {
      return {
        ...prev,
        [name]: !prev[name],
      };
    });
  };

  return (
    <>
      <Space direction="vertical">
        {mainData.map((item, index) => {
          return (
            <Space
              key={
                typeof item.label === "string"
                  ? item.label
                  : `main-data${index}`
              }
              direction="vertical"
              size={2}
              style={{ marginBottom: "10px" }}
            >
              <Typography.Text className={style.itemLabel}>
                {item.label}
              </Typography.Text>
              <Typography.Text className={style.itemValue}>
                {item.value}
              </Typography.Text>
            </Space>
          );
        })}

        <Divider style={{ marginBottom: "10px" }} />

        <Typography.Text className={style.sectionLabel}>
          Destination details
        </Typography.Text>

        {destinationData.map((item, index) => {
          return (
            <Space
              key={item.label}
              size={2}
              style={{ marginBottom: "10px" }}
              direction="vertical"
            >
              <Typography.Text className={style.itemLabel}>
                {item.label}
              </Typography.Text>
              <Typography.Text className={style.itemValue}>
                {item.value}
              </Typography.Text>
            </Space>
          );
        })}

        <Divider />

        <Space direction="vertical" size={2}>
          <Typography.Text className={style.itemLabel}>
            {isCryptoWithdrawal
              ? "Estimated Net Withdrawal Amount"
              : "Net Withdrawal Amount"}
          </Typography.Text>
          <Typography.Text className={style.itemValue}>
            {`${Big(Number(state.amount))
              .minus(transactionFee ?? 0)
              .round(isMainCurrency ? 2 : CRYPTO_PLACES)
              .toString()} ${currencyLabel()}`}

            {Big(flatFee).eq(0) && " - Blockchain fees (in kind)"}
          </Typography.Text>
        </Space>
      </Space>

      {isMainCurrency && !isCryptoWithdrawal && (
        <>
          <Divider />
          <div className={style.usdtToFiatAgreement}>
            <Checkbox
              onClick={() => onUsdtToFiatAgreement("agreement1")}
              checked={usdtCheckboxAgreements.agreement1}
              id="agreement1"
            >
              <Typography.Text>
                <label htmlFor="agreement1">
                  Kindly be aware that when initiating a fiat withdrawal, the
                  process involves converting your USDT balance to USD before
                  the withdrawal is executed. It's important to note that there
                  is a potential currency risk, as the equivalence of USDT to 1
                  USD, as displayed on the platform, may vary. The actual amount
                  will be determined at the moment your fiat withdrawal is
                  processed, and by proceeding, you acknowledge and accept any
                  currency risk linked to the USDT to USD conversion.
                </label>
              </Typography.Text>
            </Checkbox>

            <Checkbox
              onClick={() => onUsdtToFiatAgreement("agreement2")}
              checked={usdtCheckboxAgreements.agreement2}
              id="agreement2"
            >
              <Typography.Text>
                <label htmlFor="agreement2">
                  Please note that all fiat withdrawals to a bank account are
                  processed in USD, please confirm with your bank if they accept
                  USD and how they process USD. BF Virtual Assets Broker will
                  not be responsible for any currency risk or conversion charges
                  into your local currency or any loss due to funds being
                  returned back to us minus fees.
                </label>
              </Typography.Text>
            </Checkbox>
          </div>
        </>
      )}

      <NavigationFooter
        onCancel={() =>
          setState({
            ...state,
            step: isCryptoWithdrawal
              ? WithdrawalProcessSteps.WITHDRAWAL_CRYPTO
              : WithdrawalProcessSteps.WITHDRAWAL_BANK_TRANSFER,
          })
        }
        onContinue={onContinue}
        disabled={nextDisabled}
      />
    </>
  );
};
export default Summary;
